/* eslint-disable jsx-a11y/control-has-associated-label */
/* eslint-disable react/no-danger */
import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import moment from 'moment-mini';

// queries
import { usePlaceholdersByAdmin } from 'hooks/placeholders';

// components
import AvatarCell from 'shared/components/Table/CellRenderers/AvatarCell';
import { IMAGE_WITH_POPUP_TYPE } from 'core/types';
import TableHeadCellSortable from 'shared/components/Table/TableHead/TableHeadCellSortable';
import Loader from 'shared/components/Loader';
import PlaceholderActions from '../PlaceholderActions';

// icons
import { ReactComponent as ClockSvg } from 'assets/image/clock-challenges.svg';

import { PLACEHOLDER_POSSIBLE_SORT, SearchPlaceholdersSort, SortDirection } from '@joc/api-gateway';
import { resolveNextSort } from 'core/functions/sort';

import { getPlaceholderPhase, PLACEHOLDER_PHASE } from '../placeholderPhase';

// styles
import styles from './PlaceholdersTable.module.scss';

type PlaceholdersTableProps = {
	search: string;
};

const PLACEHOLDER_PHASE_ROW_CLASS: Partial<Record<PLACEHOLDER_PHASE, string>> = {
	[PLACEHOLDER_PHASE.ACTIVE]: styles.placeholdersTable__row_active,
	[PLACEHOLDER_PHASE.EXPIRED]: styles.placeholdersTable__row_expired,
};

export const PlaceholdersTable: FC<PlaceholdersTableProps> = ({ search }) => {
	const [sort, setSort] = useState<SearchPlaceholdersSort>();

	const { data, isFetching, isFetchingNextPage, hasNextPage, fetchNextPage } = usePlaceholdersByAdmin(search, sort);

	const placeholders = useMemo(() => data?.pages.flatMap(page => page.records) || [], [data]);

	const { ref, inView } = useInView({
		threshold: 1.0, // Trigger only when the element is fully in view
	});

	useEffect(() => {
		if (inView && hasNextPage) {
			fetchNextPage().finally();
		}
	}, [inView, hasNextPage, fetchNextPage]);

	const handleSortChange = useCallback(
		(sortBy: PLACEHOLDER_POSSIBLE_SORT, clickedDirection?: SortDirection) =>
			setSort(prevSort => {
				const nextSort = resolveNextSort({
					currentSort: prevSort,
					nextSortBy: sortBy,
					clickedDirection,
					allowClear: true,
				});

				return nextSort ? SearchPlaceholdersSort.fromJS(nextSort) : undefined;
			}),
		[]
	);

	const getPlainTextFromHtml = (html: string) => {
		const elem = document.createElement('div');
		elem.innerHTML = html;

		return elem.innerText;
	};

	return placeholders.length ? (
		<>
			<table className={styles.placeholdersTable}>
				<thead>
					<tr>
						<th>Photo</th>
						<th className={styles.placeholdersTable__name}>
							<TableHeadCellSortable
								title="Placeholder name"
								sortBy={PLACEHOLDER_POSSIBLE_SORT.Name}
								handleSortChange={handleSortChange}
								sort={SearchPlaceholdersSort.fromJS(sort)}
							/>
						</th>
						<th className={styles.placeholdersTable__description}>Description</th>
						<th className={styles.placeholdersTable__startDate}>Start Date</th>
						<th className={styles.placeholdersTable__endDate}>End Date</th>
						<th aria-label="Actions" />
					</tr>
				</thead>

				<tbody>
					{placeholders.map(placeholder => {
						const phase = getPlaceholderPhase(placeholder);

						return (
							<tr key={placeholder.id} className={phase ? PLACEHOLDER_PHASE_ROW_CLASS[phase] : undefined}>
								<td align="center" width="120px">
									<AvatarCell
										imagePath={placeholder.imagePath}
										opportunityType={IMAGE_WITH_POPUP_TYPE.MANUAL_OPPO}
										parentClassName={styles.placeholdersTable__photo}
									/>
								</td>
								<td className={styles.placeholdersTable__name} title={placeholder.name}>
									<span>{placeholder.name}</span>
								</td>
								<td
									className={styles.placeholdersTable__description}
									title={getPlainTextFromHtml(placeholder.description)}
								>
									<span>{getPlainTextFromHtml(placeholder.description)}</span>
								</td>
								<td className={styles.placeholdersTable__startDate}>
									{placeholder.startDate ? (
										<div className={styles.placeholdersTable__date_wrapper}>
											<ClockSvg />
											<span>{moment(placeholder.startDate).format('MMMM DD, YYYY')}</span>
										</div>
									) : (
										<span>-</span>
									)}
								</td>
								<td className={styles.placeholdersTable__endDate}>
									{placeholder.endDate ? (
										<div className={styles.placeholdersTable__date_wrapper}>
											<ClockSvg />
											<span>{moment(placeholder.endDate).format('MMMM DD, YYYY')}</span>
										</div>
									) : (
										<span>-</span>
									)}
								</td>
								<td align="center">
									<PlaceholderActions placeholder={placeholder} />
								</td>
							</tr>
						);
					})}
					<tr>
						<td>
							<div ref={ref} style={{ height: '1px', visibility: 'hidden' }} />
						</td>
					</tr>
				</tbody>
			</table>

			{isFetchingNextPage ? <Loader loadProps={{ parentClassName: styles.pagination__loader }} /> : null}
		</>
	) : !isFetching ? (
		<div className={styles.noItemsContainer}>
			<div className={styles.noItemsContainer__gradient} />
			<div className={styles.noItemsTextContainer}>
				<span className={styles.noItemsTitle}>Oops</span>
				<span className={styles.noItemsText}>
					There are currently no placeholders in the list. Create them to see them here.
				</span>
			</div>
		</div>
	) : null;
};
